import "../../style/sidebar/Notification.css";
import "../../style/sidebar/Navbar.css";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useRecoilState, useRecoilValue } from "recoil";
import { drawerOpenState, notificationOpenState } from "../../atoms/viewPadding";
import { darkModeState } from "../../atoms/darkMode";
import { totalDduBalanceState } from "../../atoms/myWallet";
import { notificationsState } from "../../atoms/notifications";
import { Badge, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Divider } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import axios from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import Basket from "./Basket";
import NavDrawerHeader from "../navbar/NavDrawerHeader";
import ProfileDetails from "../navbar/ProfileDetails";
import NavLinks from "../navbar/NavLinks";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WalletInfo from "./WalletInfo";
import NotificationList from "./NotificationList";
import LocalPrice from "../../utils/LocalPrice";

const drawerWidth = 215;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  right: 0,
  left: "unset",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
  right: 0,
  left: "unset",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "notificationOpen",
})(({ theme, notificationOpen }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(notificationOpen && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!notificationOpen && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// **** Menu ****

const menuWidth = 265;

const openedMixinMenu = (theme) => ({
  width: menuWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixinMenu = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: "0",
  justifyContent: "space-between",
});

const DrawerMenu = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "drawerOpen",
})(({ theme, drawerOpen }) => ({
  width: menuWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(drawerOpen && {
    ...openedMixinMenu(theme),
    "& .MuiDrawer-paper": openedMixinMenu(theme),
  }),
  ...(!drawerOpen && {
    ...closedMixinMenu(theme),
    "& .MuiDrawer-paper": closedMixinMenu(theme),
  }),
}));

const NotificationBar = () => {
  const navigate = useNavigate();
  const [token] = useCookies(["jwt"]);
  const jwt = token.jwt;

  // Amerika Gümrük Vergisi için özel erişim kontrolü
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.user?.id;
  const allowedEmails = ["gokay.kocken@icloud.com", "ferhatmete9224@gmail.com"];
  const userEmail = user?.user?.email;
  const canAccessNotification = allowedEmails.includes(userEmail);

  // Login kontrolü - dark mode sadece login olan kullanıcılar için
  const isLoggedIn = jwt && jwt.length > 0;

  const [notificationOpen, setNotificationOpen] = useRecoilState(notificationOpenState);
  const [drawerOpen, setDrawerOpen] = useRecoilState(drawerOpenState);
  const [darkMode, setDarkMode] = useRecoilState(darkModeState);
  const [not, setNot] = useRecoilState(notificationsState);

  const [isNotificationGet, setIsNotificationGet] = useState(false);

  // DDU Teminat Bedeli - Wallet API'sinden alınıyor
  const totalDduBalance = useRecoilValue(totalDduBalanceState);
  const [dduModalOpen, setDduModalOpen] = useState(false);

  // DDU balance'da hangi para birimi varsa onu göster - ilk dolu olan değeri göster
  const getDisplayDduBalance = () => {
    if (!totalDduBalance) return null;

    if (typeof totalDduBalance === 'object') {
      // Obje ise, ilk dolu olan değeri bul (0'dan büyük olan)
      if (totalDduBalance.total_try !== undefined && totalDduBalance.total_try > 0) {
        return { amount: totalDduBalance.total_try, currency: '₺', symbol: 'TRY' };
      }
      if (totalDduBalance.total_usd !== undefined && totalDduBalance.total_usd > 0) {
        return { amount: totalDduBalance.total_usd, currency: '$', symbol: 'USD' };
      }
      if (totalDduBalance.total_eur !== undefined && totalDduBalance.total_eur > 0) {
        return { amount: totalDduBalance.total_eur, currency: '€', symbol: 'EUR' };
      }
      if (totalDduBalance.total_gbp !== undefined && totalDduBalance.total_gbp > 0) {
        return { amount: totalDduBalance.total_gbp, currency: '£', symbol: 'GBP' };
      }
    } else if (typeof totalDduBalance === 'number' && totalDduBalance > 0) {
      return { amount: totalDduBalance, currency: '₺', symbol: 'TRY' };
    }

    return null;
  };

  const displayBalance = getDisplayDduBalance();

  const handleDrawerOpen = () => {
    setNotificationOpen(true);
  };

  const handleDrawerClose = () => {
    setNotificationOpen(false);
  };

  const handleMenuOpen = () => {
    setDrawerOpen(false);
  };

  const handleMenuClose = () => {
    setDrawerOpen(true);
  };

  const toggleDarkMode = () => {
    // Sadece login olan kullanıcılar dark mode'u kullanabilir
    if (!isLoggedIn) return;

    const newDarkMode = !darkMode;

    // Geçiş sırasında donmayı önlemek için transition'ları geçici olarak devre dışı bırak
    document.body.classList.add('dark-mode-transitioning');

    // Önce localStorage'a kaydet
    localStorage.setItem('darkMode', newDarkMode);

    // State'i güncelle
    setDarkMode(newDarkMode);

    // Body ve HTML class'larını güncelle
    if (newDarkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark-mode');
    }

    // Kısa bir süre sonra transition'ları tekrar etkinleştir
    setTimeout(() => {
      document.body.classList.remove('dark-mode-transitioning');
    }, 50);
  };

  const getNotification = useCallback(async () => {
    await axios
      .get("notification", {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      })
      .then((response) => {
        if (response.data.data.length === 0) {
          setNotificationOpen(false);
        }
        setNot(response.data.data);
      });
  }, [setNotificationOpen, jwt]);

  const handleNavigate = useCallback(() => {
    navigate("/profile-settings/general");
  }, [navigate]);

  useEffect(() => {
    if (isNotificationGet === false) {
      getNotification();
      setIsNotificationGet(true);
    }
  }, [isNotificationGet, getNotification]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setDrawerOpen(false);
    }
  }, [setDrawerOpen]);

  // Login olduktan sonra localStorage'dan dark mode tercihini yükle
  useEffect(() => {
    if (isLoggedIn) {
      const savedDarkMode = localStorage.getItem('darkMode') === 'true';
      setDarkMode(savedDarkMode);

      if (savedDarkMode) {
        document.body.classList.add('dark-mode');
        document.documentElement.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
        document.documentElement.classList.remove('dark-mode');
      }
    }
  }, [isLoggedIn, setDarkMode]);

  return (
    <div className="notification">
      <div className="drawer-arrow-wrapper">
        <div className="drawer-arrow-contain">
          <div className="drawer-left">
            {drawerOpen ? (
              <IconButton className="menu-arrow" onClick={handleMenuOpen}>
                <ChevronLeftIcon />
              </IconButton>
            ) : (
              <IconButton className="menu-arrow" onClick={handleMenuClose}>
                <MenuOutlinedIcon />
              </IconButton>
            )}
            <img
              style={{ cursor: "pointer" }}
              onClick={() => {
                navigate("/dashboard");
              }}
              src="/images/home/logo.svg"
              alt="iyziShip"
            />
            {/* DDU Teminat Bedeli - Sadece balance > 0 ise göster */}
            {displayBalance && (
              <Tooltip title={"DDU Teminat Bedeli - Detayları görmek için tıklayın"} placement="bottom">
                <Button
                  onClick={() => setDduModalOpen(true)}
                  sx={{
                    display: "flex",
                    gap: "var(--gap)",
                    alignItems: "center",
                    border: "1px solid var(--inputBorderColor)",
                    padding: "0 10px",
                    borderRadius: "var(--inputBorderRadius)",
                    height: "30px",
                    alignSelf: "center",
                    color: "rgba(0, 0, 0, 0.54)",
                    position: "relative",
                    marginLeft: "10px",
                    zIndex: 1201,
                    minWidth: "90px",
                    ":hover": {
                      backgroundColor: "var(--pastelBlue)",
                    },
                  }}
                >
                  <AccountBalanceWalletIcon />
                  <span style={{ color: "var(--btnBgBlue)" }}>
                    {LocalPrice(displayBalance.amount)} {displayBalance.currency}
                  </span>
                </Button>
              </Tooltip>
            )}
          </div>
          <div className="drawer-right">
            {/* Dark Mode Toggle Button - Sadece login olan kullanıcılar için */}
            {isLoggedIn && canAccessNotification && (
              <Tooltip title={darkMode ? "Açık Mod" : "Koyu Mod"} placement="bottom">
                <Button
                  onClick={toggleDarkMode}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid var(--inputBorderColor)",
                    padding: "0 8px",
                    borderRadius: "var(--inputBorderRadius)",
                    height: "30px",
                    minWidth: "40px",
                    alignSelf: "center",
                    color: "var(--priceText)",
                    position: "relative",
                    marginRight: "10px",
                    zIndex: 1201,
                    ":hover": {
                      backgroundColor: "var(--pastelBlue)",
                    },
                  }}
                >
                  {darkMode ? (
                    <LightModeIcon sx={{ width: "18px", height: "18px" }} />
                  ) : (
                    <DarkModeIcon sx={{ width: "18px", height: "18px" }} />
                  )}
                </Button>
              </Tooltip>
            )}
            <WalletInfo />
            <Basket />
            <Tooltip title={"Profil Ayarları"} placement="bottom">
              <Button onClick={handleNavigate} className="drawer-settings">
                <SettingsOutlinedIcon />
              </Button>
            </Tooltip>
            <Tooltip title={"Bildirimler"} placement="bottom">
              {notificationOpen ? (
                <IconButton className="drawer-arrow" onClick={handleDrawerClose}>
                  <ChevronRightIcon />
                </IconButton>
              ) : (
                <IconButton className="drawer-arrow" onClick={handleDrawerOpen}>
                  <Badge badgeContent={not.length} overlap="circular" color="primary">
                    <NotificationsOutlinedIcon color="action" />
                  </Badge>
                </IconButton>
              )}
            </Tooltip>
          </div>
        </div>
      </div>
      <DrawerMenu className="navbar-drawer" variant="permanent" drawerOpen={drawerOpen}>
        <NavDrawerHeader />
        <ProfileDetails />
        <NavLinks />
      </DrawerMenu>
      <Drawer
        className="notification-drawer"
        variant="permanent"
        notificationOpen={notificationOpen}
      >
        <div
          style={{
            gap: "var(--gap)",
            padding: "10px",
            display: "flex",
            flexDirection: "column-reverse",
          }}
          className="notifications"
        >
          {not.length > 0 ? (
            [...not]
              .reverse()
              .map((e, key) => (
                <NotificationList key={key} notification={e} not={not} setNot={setNot} />
              ))
          ) : (
            <div
              style={{
                marginTop: "10px",
                whiteSpace: notificationOpen ? "break-spaces" : "inherit",
                backgroundColor: "var(--btnBgBlue)",
                color: "var(--lighterBg)",
                boxShadow: "var(--shadow)",
                borderRadius: "var(--buttonBorderRadius)",
                padding: "5px",
              }}
            >
              Bildiriminiz Bulunmamaktadır.
            </div>
          )}
        </div>
      </Drawer>

      {/* DDU Teminat Bedeli Modal */}
      <Dialog
        open={dduModalOpen}
        onClose={() => setDduModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{
          background: "linear-gradient(135deg, var(--btnBgBlue) 0%, #1976d2 100%)",
          color: "white",
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "1.5rem",
          padding: "24px",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "60px",
            height: "4px",
            backgroundColor: "rgba(255,255,255,0.3)",
            borderRadius: "2px"
          }
        }}>
          💰 DDU Teminat Bedeli Detayları
        </DialogTitle>
        <DialogContent sx={{
          padding: "32px 24px",
          backgroundColor: "#fafafa"
        }}>
          {typeof totalDduBalance === 'object' ? (
            <Box>
              <Box sx={{
                textAlign: "center",
                marginBottom: "24px",
                padding: "16px",
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
              }}>
                <Typography variant="h6" sx={{
                  color: "var(--btnBgBlue)",
                  fontWeight: "600",
                  marginBottom: "8px"
                }}>
                  📊 Para Birimi Bazında Teminat Bedelleri
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Mevcut teminat bedelleriniz aşağıda listelenmiştir
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {totalDduBalance.total_try !== undefined && (
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                    borderRadius: "12px",
                    border: "1px solid #e1f5fe",
                    boxShadow: "0 4px 12px rgba(33, 150, 243, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(33, 150, 243, 0.15)"
                    }
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Box sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "var(--btnBgBlue)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "1.2rem"
                      }}>
                        ₺
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "600", color: "#1976d2" }}>
                          Türk Lirası
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                          TRY
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="h5" sx={{
                      color: "var(--btnBgBlue)",
                      fontWeight: "700",
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                    }}>
                      {LocalPrice(totalDduBalance.total_try)} ₺
                    </Typography>
                  </Box>
                )}

                {totalDduBalance.total_usd !== undefined && (
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    background: "linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)",
                    borderRadius: "12px",
                    border: "1px solid #e8f5e8",
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(76, 175, 80, 0.15)"
                    }
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Box sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#4caf50",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "1.2rem"
                      }}>
                        $
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "600", color: "#2e7d32" }}>
                          Amerikan Doları
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                          USD
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="h5" sx={{
                      color: "#2e7d32",
                      fontWeight: "700",
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                    }}>
                      {LocalPrice(totalDduBalance.total_usd)} $
                    </Typography>
                  </Box>
                )}

                {totalDduBalance.total_eur !== undefined && (
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    background: "linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)",
                    borderRadius: "12px",
                    border: "1px solid #fff3e0",
                    boxShadow: "0 4px 12px rgba(255, 193, 7, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(255, 193, 7, 0.15)"
                    }
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Box sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#ff9800",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "1.2rem"
                      }}>
                        €
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "600", color: "#e65100" }}>
                          Euro
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                          EUR
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="h5" sx={{
                      color: "#e65100",
                      fontWeight: "700",
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                    }}>
                      {LocalPrice(totalDduBalance.total_eur)} €
                    </Typography>
                  </Box>
                )}

                {totalDduBalance.total_gbp !== undefined && (
                  <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                    borderRadius: "12px",
                    border: "1px solid #f3e5f5",
                    boxShadow: "0 4px 12px rgba(156, 39, 176, 0.1)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(156, 39, 176, 0.15)"
                    }
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Box sx={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#9c27b0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "1.2rem"
                      }}>
                        £
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: "600", color: "#7b1fa2" }}>
                          İngiliz Sterlini
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                          GBP
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="h5" sx={{
                      color: "#7b1fa2",
                      fontWeight: "700",
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                    }}>
                      {LocalPrice(totalDduBalance.total_gbp)} £
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{
              textAlign: "center",
              padding: "40px",
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
            }}>
              <Typography variant="h4" sx={{
                color: "var(--btnBgBlue)",
                fontWeight: "700",
                marginBottom: "8px"
              }}>
                {LocalPrice(totalDduBalance)} ₺
              </Typography>
              <Typography variant="body1" sx={{ color: "#666" }}>
                DDU Teminat Bedeli
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          padding: "20px 24px",
          backgroundColor: "#f5f5f5",
          borderTop: "1px solid #e0e0e0"
        }}>
          <Button
            onClick={() => setDduModalOpen(false)}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "var(--btnBgBlue)",
              color: "white",
              borderRadius: "8px",
              padding: "12px 32px",
              fontWeight: "600",
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
              "&:hover": {
                backgroundColor: "#1565c0",
                color: "white",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)"
              }
            }}
          >
            ✨ Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NotificationBar;
